import type { Account } from "./account";
import type { Card } from "./card";
import type { Contact } from "./contact";
import type { SalesChannel, OrderStatus, OrderType, ValuesOf, LocationType } from "./enums";
import { id } from "./id";
import type { address, Location } from "./location";
import type { OrderItem } from "./order_item";
import type { Zone } from "./zone";

export interface order_key {
    id: string;
    sellerId: number;
    buyerId: number;
    orderId: number;
}

export interface order extends order_key {
    sellerLocationId: number;
    buyerLocationId: number;
    sellerLocation: address & { name: string, type: ValuesOf<typeof LocationType> };
    buyerLocation: address & { name: string, type: ValuesOf<typeof LocationType> };
    zoneId?: number;
    completedId?: number;
    coupon: string;
    status: ValuesOf<typeof OrderStatus>;
    type: ValuesOf<typeof OrderType>;
    channel: ValuesOf<typeof SalesChannel>;
    notes: string;
    phone?: string;
    loyalty: number;
    taxRate: number;
    items: number;
    total: number;
    sellerSatisfaction: number;
    buyerSatisfaction: number;
    completed?: string;
}

export interface Order extends order {
    cardId?: number;
    card?: string;
    buyer: string;
    seller: string;
    zone?: string;
    buyerImage?: string;
    sellerImage?: string;
    details: OrderItem[];
}

export const orderToBuyer = (x: Order): Contact => ({ id: id(x.sellerId, x.buyerId), name: x.buyer, image: x.buyerImage }) as unknown as Contact;
export const orderToSeller = (x: Order): Contact => ({ id: id(x.buyerId, x.sellerId), name: x.seller, image: x.sellerImage }) as unknown as Contact;
export const orderToDriver = (x: Order): Account => ({ id: id(x.completedId), name: x.completed, image: null }) as unknown as Account;

export const orderToBuyerLocation = (x: Order): Location =>
    ({
        id: id(x.buyerId, x.buyerLocationId),
        accountId: x.buyerId,
        locationId: x.buyerLocationId!,
        name: x.buyerLocation?.name,
    }) as Location;

export const orderToSellerLocation = (x: Order): Location =>
    ({
        id: id(x.sellerId, x.sellerLocationId),
        accountId: x.sellerId,
        locationId: x.sellerLocationId!,
        name: x.sellerLocation?.name,
    }) as Location;

export const orderToZone = (x: Order): Zone =>
    ({
        id: id(x.sellerId, x.zoneId),
        accountId: x.sellerId,
        zoneId: x.zoneId!,
        name: x.zone!,
    }) as Zone;

export const orderToCard = (x: Order): Card =>
    ({
        id: id(x.buyerId, x.cardId),
        accountId: x.buyerId,
        cardId: x.cardId!,
        lastFour: x.card!,
    }) as Card;
